/* ==========================================================================
   Wattec — Home page behaviour

   Two things live here: the hero slider and the arrow controls on the
   horizontal product rail. Both are progressive enhancement. With this file
   missing the hero still shows its first slide, the product rail is still a
   normal scrollable strip you can swipe, and the FAQ still opens, because
   those are real <details> elements.

   Reduced-motion preferences are honoured: autoplay never starts, and the
   slider jumps rather than glides.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ===================================================== hero slider ===== */
  function initHero(root) {
    var track = root.querySelector('[data-hx-track]');
    if (!track) return;

    var slides = root.querySelectorAll('[data-hx-slide]');
    var segs = root.querySelectorAll('[data-hx-seg]');
    var prev = root.querySelector('[data-hx-prev]');
    var next = root.querySelector('[data-hx-next]');
    var count = slides.length;

    if (count < 2) {
      // Nothing to move between — hide the controls rather than leave dead
      // buttons and a single progress segment on the page.
      var bar = root.querySelector('[data-hx-controls]');
      if (bar) bar.hidden = true;
      var prog = root.querySelector('[data-hx-progress]');
      if (prog) prog.hidden = true;
      return;
    }

    var index = 0;
    var timer = null;
    var interval = parseInt(root.dataset.hxInterval || '7000', 10);
    var autoplay = root.dataset.hxAutoplay === 'true' && !reduceMotion;

    if (reduceMotion) root.classList.add('wt-hx--noanim');

    function paint() {
      track.style.transform = 'translateX(' + -index * 100 + '%)';

      for (var i = 0; i < count; i++) {
        var isCurrent = i === index;
        slides[i].setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
        // Keep off-screen slides out of the tab order so keyboard users are
        // not taken to buttons they cannot see.
        var focusables = slides[i].querySelectorAll('a, button');
        for (var f = 0; f < focusables.length; f++) {
          if (isCurrent) focusables[f].removeAttribute('tabindex');
          else focusables[f].setAttribute('tabindex', '-1');
        }
      }

      for (var s = 0; s < segs.length; s++) {
        var seg = segs[s];
        seg.classList.remove('is-timing');
        seg.classList.toggle('is-active', s <= index);
        seg.setAttribute('aria-selected', s === index ? 'true' : 'false');
      }

      // Re-arm the fill animation on the current segment.
      if (autoplay && segs[index]) {
        var active = segs[index];
        active.classList.remove('is-active');
        // Force a reflow so the width resets to 0 before the timed fill.
        void active.offsetWidth;
        active.classList.add('is-timing', 'is-active');
      }
    }

    function go(to) {
      index = (to + count) % count;
      paint();
      restart();
    }

    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    function restart() {
      stop();
      if (!autoplay) return;
      timer = window.setInterval(function () { go(index + 1); }, interval);
    }

    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });

    for (var s2 = 0; s2 < segs.length; s2++) {
      (function (i) {
        segs[i].addEventListener('click', function () { go(i); });
      })(s2);
    }

    // Arrow keys when the slider itself has focus.
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { go(index - 1); }
      else if (event.key === 'ArrowRight') { go(index + 1); }
    });

    // Pause while someone is reading or interacting.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', restart);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', restart);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else restart();
    });

    // Swipe. Only a clearly horizontal drag counts, so vertical page
    // scrolling on a phone is never hijacked.
    var startX = 0, startY = 0, tracking = false;
    root.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      tracking = true;
      stop();
    }, { passive: true });

    root.addEventListener('touchend', function (event) {
      if (!tracking) return;
      tracking = false;
      var touch = event.changedTouches[0];
      var dx = touch.clientX - startX;
      var dy = touch.clientY - startY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        go(dx < 0 ? index + 1 : index - 1);
      } else {
        restart();
      }
    }, { passive: true });

    // Only run the slider once it is actually on screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) restart(); else stop();
        });
      }, { threshold: 0.2 }).observe(root);
    }

    paint();
    restart();
  }

  /* ============================================== horizontal rail ======== */
  function initRail(root) {
    var rail = root.querySelector('[data-rail]');
    if (!rail) return;

    var prev = root.querySelector('[data-rail-prev]');
    var next = root.querySelector('[data-rail-next]');
    if (!prev && !next) return;

    function step() {
      var first = rail.querySelector('li, article, div');
      if (!first) return rail.clientWidth * 0.8;
      var styles = window.getComputedStyle(rail);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function sync() {
      // 2px of slack, because sub-pixel widths mean the scroll position
      // rarely lands exactly on the maximum.
      var max = rail.scrollWidth - rail.clientWidth - 2;
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max;
    }

    function scrollBy(direction) {
      rail.scrollBy({
        left: step() * direction,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
    if (next) next.addEventListener('click', function () { scrollBy(1); });

    var raf = null;
    rail.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () { raf = null; sync(); });
    }, { passive: true });

    window.addEventListener('resize', sync);
    sync();
    // Images settling can change the scroll width after first paint.
    window.setTimeout(sync, 800);
  }

  /* =================================================== bootstrap ========= */
  function init(scope) {
    var context = scope || document;

    var heroes = context.querySelectorAll('[data-wt-hero]');
    Array.prototype.forEach.call(heroes, function (el) {
      if (el.dataset.wtHomeReady === '1') return;
      el.dataset.wtHomeReady = '1';
      initHero(el);
    });

    var rails = context.querySelectorAll('[data-wt-rail-root]');
    Array.prototype.forEach.call(rails, function (el) {
      if (el.dataset.wtHomeReady === '1') return;
      el.dataset.wtHomeReady = '1';
      initRail(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Re-run when a section is added or reloaded in the theme customiser.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.querySelectorAll) {
      Array.prototype.forEach.call(
        target.querySelectorAll('[data-wt-home-ready]'),
        function (el) { delete el.dataset.wtHomeReady; }
      );
    }
    init(target);
  });
})();
